import React, { useEffect, useState } from "react";
import { Box, Button, Collapse, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import FiltersBarCst from "@components/FiltersBarCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { handleFiltersEnter } from "@utils/utilsFiltersKeyboard";
import { FiltersAccordionProps } from "./types";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";

const FiltersAccordionCst: React.FC<FiltersAccordionProps> = ({
  open,
  onClose,
  pageId,
  value,
  onApply,
  extraFields,
  excludeKeys,
  filterVisibility,
  dynamicOptions,
  optionsKeyOverrides,
  optionsToDisable,
  title,
}) => {
  const { t } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [draft, setDraft] = useState<FiltersBarValue>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const handleReset = () => setDraft({});

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Collapse in={open} unmountOnExit>
      <Box
        onKeyDown={(e) => handleFiltersEnter(e, handleApply)}
        sx={{
          mt: 2,
          mb: 2,
          padding: "20px 24px",
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          {title ?? t("filters-modal.title")}
        </Typography>

        <Box
          sx={{
            "& .MuiGrid-container": { marginTop: 0 },
            "& .MuiGrid-item": { paddingTop: "16px !important" },
          }}
        >
          <FiltersBarCst
            pageId={pageId}
            value={draft}
            onChange={setDraft}
            extraFields={extraFields}
            excludeKeys={excludeKeys}
            filterVisibility={filterVisibility}
            dynamicOptions={dynamicOptions}
            optionsKeyOverrides={optionsKeyOverrides}
            optionsToDisable={optionsToDisable}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            mt: 2,
          }}
        >
          <Box sx={{ flex: 1 }} />
          <ButtonTertiary onClick={handleReset}>
            {t("filters-modal.reset")}
          </ButtonTertiary>
          <ButtonPrimary onClick={handleApply}>
            {t("filters-modal.apply")}
          </ButtonPrimary>
        </Box>
      </Box>
    </Collapse>
  );
};

export default FiltersAccordionCst;
